/** @format */

import { makeStyles } from '@material-ui/core';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import adescomStyle from '../../../assets/jss/adescom/adescomStyle';
import { CustomMinimalTabs } from '../../../components/CustomMinimalTabs/CustomMinimalTabs';
import GeneralDialog from '../../../components/Dialog/GeneralDialog';
import InputFormRest from '../../../components/Form/InputFormRest';
import GridContainer from '../../../components/Grid/GridContainer';
import GridItem from '../../../components/Grid/GridItem';
import { configDefault } from '../../../core/config';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import { FormGeneralHelper } from '../../../helpers/FormGeneral';
import { NotificationHelper } from '../../../helpers/NotificationHelper';
import { PricelistSchemaHelper } from '../../../helpers/PricelistSchemaHelper';
import { roles, UserRoleHelper } from '../../../helpers/UserRoleHelper';
import PricelistSchemaInterface from '../../../interfaces/billing/PricelistSchemaInterface';
import { pricelistSchemaConnectorConstants, pricelistSchemaConstants } from '../../../modules/pricelists/reducers';
import PricelistAssignment from './PricelistSchemaElementDialog/PricelistAssignment';
import PricelistSchemaElementInterface from '../../../interfaces/billing/PricelistSchemaElementInterface';
import PricelistSchemaConnectorInterface from '../../../interfaces/billing/PricelistSchemaConnectorInterface';
import { PricelistSchemaConnectorsCollectionInterface } from '../../../interfaces/api/PricelistSchemaConnectorsCollectionInterface';
import i18n from '../../../core/i18n';
import { DateHelper } from '../../../helpers/DateHelper';
import CustomDatePicker from '../../../components/Form/CustomDatePicker';
import oplAppStyle from '../../../assets/themes/OPL/jss/App/OplAppStyle';
import { withStyles } from '@material-ui/core/styles';

interface PricelistSchemaEditProps {
    classes: any;
    pricelistSchema?: PricelistSchemaInterface;
    editMode?: boolean;
    pricelistSchemaDialogOpen: boolean;
    togglePricelistDialog: () => void;
    loadPricelistSchemaList: () => void;
    setActivePricelistSchema: React.Dispatch<React.SetStateAction<number | null>>;
    activePricelistSchema: number | null;
    setPricelistSchemaDialogOpen: (value: boolean) => void;
}

const PricelistSchemaInitialState: PricelistSchemaInterface = {
    mainname: '',
    activated: false,
    activatedAt: null,
};

const PricelistSchemaEdit = (props: PricelistSchemaEditProps): React.ReactElement => {
    const dispatch = useDispatch();

    const [entityStatus, setEntityStatus] = useState<any>({
        mainname: null,
        activated: null,
        activatedAt: null,
    });

    const [activePricelistSchema, setActivePricelistSchema] = useState<number | null>(null);

    const [pricelistSchemaElements, setPricelistSchemaElements] = useState<Array<PricelistSchemaElementInterface>>([]);
    const [
        pricelistSchemaElementsFullResponse,
        setPricelistSchemaElementsFullResponse,
    ] = useState<PricelistSchemaConnectorsCollectionInterface | null>(null);
    const [oldPricelistSchemaElements, setOldPricelistSchemaElements] = useState<Array<PricelistSchemaElementInterface>>([]);

    const PRICELIST_URI = '/pricelist_schemas';
    const PRICELIST_CONNECTOR_URI = '/pricelist_schema_connectors';
    const [entity, setEntity] = useState<PricelistSchemaInterface>(
        props.pricelistSchema
            ? {
                  mainname: props.pricelistSchema.mainname,
                  activated: props.pricelistSchema.activated,
                  activatedAt: props.pricelistSchema.activatedAt,
              }
            : PricelistSchemaInitialState
    );

    const changeInput = (e: any, stateName: string, type: string, require: boolean, maxValue: number, minValue: number): void => {
        // TODO: Add validation (use entityStatus)
        setEntity({ ...entity, [stateName]: e.target.value });
    };

    const changePicker = (moment: any): void => {
        setEntity({ ...entity, activatedAt: DateHelper.format(moment) });
    };

    const loadPricelistElements = useCallback(
        (pricelistId: number | null, uri?: string) => {
            if (!uri) {
                uri = FormGeneralHelper.getPath(`${PRICELIST_CONNECTOR_URI}`, configDefault.PLATFORM_API_PREFIX);
            }

            if (!pricelistId) {
                return;
            }

            const itemLoaders = new ItemLoaders(dispatch);
            itemLoaders.getListWithCallback(
                FormGeneralHelper.getByUri(uri),
                pricelistSchemaConnectorConstants.LIST,
                [
                    { name: 'pricelistSchemaId', value: pricelistId },
                    { name: 'itemsPerPage', value: 999999 },
                ],
                [],
                (data: PricelistSchemaConnectorsCollectionInterface) => {
                    if (data['hydra:member']) {
                        const pricelistElements = data['hydra:member'].map((el: PricelistSchemaConnectorInterface) => {
                            return el.pricelistSchemaElement;
                        });
                        // TODO: Optimize for less setState, make pricelistSchemaConnector as a dispatch not setState.
                        setPricelistSchemaElementsFullResponse(data);
                        setPricelistSchemaElements(pricelistElements);
                        setOldPricelistSchemaElements(pricelistElements);
                    }
                },
                {
                    propagateDataToCallback: true,
                    dispatchAfterCallback: false,
                }
            );
        },
        [dispatch]
    );

    const loadPricelist = useCallback(() => {
        const itemLoaders = new ItemLoaders(dispatch);
        itemLoaders.getWithCallback(
            FormGeneralHelper.getByUri(
                FormGeneralHelper.getPath(`${PRICELIST_URI}/${props.activePricelistSchema}`, configDefault.PLATFORM_API_PREFIX)
            ),
            pricelistSchemaConstants.ITEM,
            (data: PricelistSchemaInterface) => {
                setEntity(data);
                if (data.id) {
                    setActivePricelistSchema(data.id);
                    loadPricelistElements(data.id);
                }
            },
            {
                propagateDataToCallback: true,
                dispatchAfterCallback: false,
            }
        );
    }, [dispatch, props.activePricelistSchema, loadPricelistElements]);

    const checkForPricelistSchemaElementsChange = (): boolean => {
        if (pricelistSchemaElements.length === oldPricelistSchemaElements.length) {
            const schemaElementsIds: Array<number> = [];
            const oldSchemaElementsIds: Array<number> = [];
            pricelistSchemaElements.forEach((pse: PricelistSchemaElementInterface) => {
                if (pse.id) {
                    schemaElementsIds.push(pse.id);
                }
            });
            oldPricelistSchemaElements.forEach((opse: PricelistSchemaElementInterface) => {
                if (opse.id) {
                    oldSchemaElementsIds.push(opse.id);
                }
            });

            schemaElementsIds.sort();
            oldSchemaElementsIds.sort();

            const schemaToString = JSON.stringify(schemaElementsIds);
            const oldSchemaToString = JSON.stringify(oldSchemaElementsIds);

            if (schemaToString === oldSchemaToString) {
                return false;
            } else {
                return true;
            }
        } else {
            return true;
        }
    };

    const savePricelistSchema = (): void => {
        const itemLoaders = new ItemLoaders(dispatch);
        let entityToChange = { ...entity };
        let params: any = {};
        params[PricelistSchemaHelper.PRICELIST_SCHEMA_ELEMENT_CONSTANTS.PRICELIST_SCHEMA_ELEMENT] = pricelistSchemaElements.map((el: any) => {
            return el.id;
        });
        const pricelistChange = checkForPricelistSchemaElementsChange();
        if (pricelistChange) {
            entityToChange.params = JSON.stringify(params);
        }
        if (entityToChange.mainname.trim().length === 0) {
            NotificationHelper.notifyError('pricelists:form.pricelist_short_name', dispatch);
            return;
        }

        if (props.activePricelistSchema) {
            itemLoaders.putWithCallback(
                FormGeneralHelper.getByUri(
                    FormGeneralHelper.getPath(`${PRICELIST_URI}/${props.activePricelistSchema}`, configDefault.PLATFORM_API_PREFIX)
                ),
                pricelistSchemaConstants.EDIT,
                entityToChange,
                (data: PricelistSchemaInterface) => {
                    props.loadPricelistSchemaList();
                    props.setPricelistSchemaDialogOpen(false);
                    props.setActivePricelistSchema(null);
                },
                {
                    propagateDataToCallback: true,
                }
            );
        } else {
            itemLoaders.postWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath(PRICELIST_URI, configDefault.PLATFORM_API_PREFIX)),
                pricelistSchemaConstants.ADD,
                entityToChange,
                (data: PricelistSchemaInterface) => {
                    props.loadPricelistSchemaList();
                    props.setPricelistSchemaDialogOpen(false);
                    props.setActivePricelistSchema(null);
                },
                {
                    propagateDataToCallback: true,
                }
            );
        }
    };

    useEffect(() => {
        if (props.activePricelistSchema) {
            loadPricelist();
        }
    }, [loadPricelist, props.activePricelistSchema]);

    useEffect(() => {
        if (!props.pricelistSchemaDialogOpen) {
            setPricelistSchemaElements([]);
            setEntity(PricelistSchemaInitialState);
        }
    }, [props.pricelistSchemaDialogOpen]);

    return (
        <GeneralDialog
            maxWidth={'lg'}
            open={props.pricelistSchemaDialogOpen}
            dialogType={'action'}
            fullWidth
            title={'pricelists:t.add_pricelist_form'}
            disagreeCallback={props.togglePricelistDialog}
            disagreeCallbackTextLabel={'general:common.cancel'}
            agreeCallback={
                UserRoleHelper.checkRole(
                    props.activePricelistSchema ? roles.ROLE_PLATFORM_PRICELIST_SCHEMA_PUT : roles.ROLE_PLATFORM_PRICELIST_SCHEMA_POST
                )
                    ? savePricelistSchema
                    : (): void => {}
            }
            agreeCallbackTextLabel={'general:common.save'}
        >
            <GridContainer>
                <GridItem xs={12} md={12} lg={12}>
                    <CustomMinimalTabs
                        minHeight={500}
                        resetActiveCard={props.pricelistSchemaDialogOpen}
                        tabs={[
                            {
                                name: 'pricelists:b.pricelist_element_details',
                                element: (
                                    <div style={{ maxWidth: 300 }}>
                                        <InputFormRest
                                            classes={props.classes}
                                            callback={changeInput}
                                            stateName="mainname"
                                            stateObjectState={entityStatus.mainname}
                                            typeCallback={FormGeneralHelper.TYPE_TEXT}
                                            typeField={FormGeneralHelper.TYPE_TEXT}
                                            displayFieldName="pricelists:h.pricelist_schema_name"
                                            required={false}
                                            vMax={255}
                                            vMin={0}
                                            value={entity.mainname}
                                        />
                                        <CustomDatePicker
                                            fieldName={'gracePeriod'}
                                            dateFormat={'YYYY-MM-DD HH:mm'}
                                            timeFormat={true}
                                            autoComplete={'off'}
                                            placeholder={'YYYY-MM-DD HH:mm'}
                                            defaultValue={entity.activatedAt}
                                            helpText="pricelists:h.activated_at"
                                            callback={changePicker}
                                        />
                                    </div>
                                ),
                            },
                            {
                                name: 'pricelists:b.pricelist_element_add',
                                element: (
                                    <PricelistAssignment
                                        activePricelistSchema={activePricelistSchema}
                                        pricelistSchemaElementsFullResponse={pricelistSchemaElementsFullResponse}
                                        loadPricelistElements={loadPricelistElements}
                                        setPricelistSchemaElement={setPricelistSchemaElements}
                                        pricelistSchemaElements={pricelistSchemaElements}
                                    />
                                ),
                            },
                        ]}
                    />
                </GridItem>
            </GridContainer>
        </GeneralDialog>
    );
};

export default withStyles(oplAppStyle)(PricelistSchemaEdit);
